/**
 * EAA Compliant Announcement Bar Keyboard Navigation
 * Ensures keyboard accessibility for Shopify announcement bar carousel
 */

class AnnouncementBarAccessibility {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupKeyboardNavigation());
    } else {
      this.setupKeyboardNavigation();
    }
  }

  setupKeyboardNavigation() {
    const sliders = document.querySelectorAll('.slider--everywhere');
    
    sliders.forEach(slider => {
      if (!slider.id.includes('Slider-')) return; // Only announcement bar sliders
      
      const container = slider.closest('slideshow-component');
      if (!container) return;

      const prevButton = container.querySelector('.slider-button--prev');
      const nextButton = container.querySelector('.slider-button--next');
      
      // Add keyboard event listeners
      slider.addEventListener('keydown', (e) => this.handleKeydown(e, prevButton, nextButton, slider));
      
      // Ensure proper focus management
      this.setupFocusManagement(slider);
      
      // Add focus indicator styles
      this.addFocusStyles(slider);
    });
  }

  handleKeydown(event, prevButton, nextButton, slider) {
    let handled = false;

    switch(event.key) {
      case 'ArrowLeft':
      case 'ArrowUp':
        event.preventDefault();
        if (prevButton && !prevButton.disabled) {
          prevButton.click();
          this.announceSlideFocus(slider, 'previous');
        }
        handled = true;
        break;

      case 'ArrowRight':
      case 'ArrowDown':
        event.preventDefault();
        if (nextButton && !nextButton.disabled) {
          nextButton.click();
          this.announceSlideFocus(slider, 'next');
        }
        handled = true;
        break;

      case 'Home':
        event.preventDefault();
        this.goToFirstSlide(slider);
        handled = true;
        break;

      case 'End':
        event.preventDefault();
        this.goToLastSlide(slider);
        handled = true;
        break;

      case 'Space':
      case 'Enter':
        event.preventDefault();
        this.activateCurrentSlideLink(slider);
        handled = true;
        break;
    }

    if (handled) {
      event.stopPropagation();
    }
  }

  setupFocusManagement(slider) {
    // Monitor slide changes and update ARIA attributes
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && 
            (mutation.attributeName === 'aria-hidden' || mutation.attributeName === 'class')) {
          this.updateSlideAria(slider);
        }
      });
    });

    const slides = slider.querySelectorAll('.slideshow__slide');
    slides.forEach(slide => {
      observer.observe(slide, { 
        attributes: true, 
        attributeFilter: ['aria-hidden', 'class'] 
      });
    });
  }

  updateSlideAria(slider) {
    const slides = slider.querySelectorAll('.slideshow__slide');
    const activeSlide = Array.from(slides).find(slide => 
      !slide.hasAttribute('aria-hidden') || slide.getAttribute('aria-hidden') === 'false'
    );

    slides.forEach((slide, index) => {
      if (slide === activeSlide) {
        slide.setAttribute('aria-current', 'true');
        slide.removeAttribute('tabindex');
      } else {
        slide.removeAttribute('aria-current');
        slide.setAttribute('tabindex', '-1');
      }
    });
  }

  goToFirstSlide(slider) {
    const slides = slider.querySelectorAll('.slideshow__slide');
    if (slides.length > 0) {
      this.activateSlide(slider, 0);
    }
  }

  goToLastSlide(slider) {
    const slides = slider.querySelectorAll('.slideshow__slide');
    if (slides.length > 0) {
      this.activateSlide(slider, slides.length - 1);
    }
  }

  activateSlide(slider, index) {
    const container = slider.closest('slideshow-component');
    const prevButton = container.querySelector('.slider-button--prev');
    const nextButton = container.querySelector('.slider-button--next');
    
    // Simulate navigation to target slide
    const currentActiveIndex = this.getCurrentSlideIndex(slider);
    const steps = index - currentActiveIndex;
    
    if (steps > 0) {
      for (let i = 0; i < steps; i++) {
        setTimeout(() => nextButton && nextButton.click(), i * 100);
      }
    } else if (steps < 0) {
      for (let i = 0; i < Math.abs(steps); i++) {
        setTimeout(() => prevButton && prevButton.click(), i * 100);
      }
    }
  }

  getCurrentSlideIndex(slider) {
    const slides = slider.querySelectorAll('.slideshow__slide');
    return Array.from(slides).findIndex(slide => 
      !slide.hasAttribute('aria-hidden') || slide.getAttribute('aria-hidden') === 'false'
    );
  }

  activateCurrentSlideLink(slider) {
    const activeSlide = slider.querySelector('.slideshow__slide:not([aria-hidden="true"])');
    if (activeSlide) {
      const link = activeSlide.querySelector('.announcement-bar__link');
      if (link) {
        link.click();
      }
    }
  }

  announceSlideFocus(slider, direction) {
    const activeSlide = slider.querySelector('.slideshow__slide:not([aria-hidden="true"])');
    if (activeSlide) {
      const announcement = activeSlide.querySelector('.announcement-bar__message span');
      if (announcement) {
        // Create temporary screen reader announcement
        this.announceToScreenReader(
          `${direction === 'previous' ? 'Previous' : 'Next'} announcement: ${announcement.textContent}`
        );
      }
    }
  }

  announceToScreenReader(message) {
    const announcement = document.createElement('div');
    announcement.setAttribute('aria-live', 'polite');
    announcement.setAttribute('aria-atomic', 'true');
    announcement.className = 'visually-hidden';
    announcement.textContent = message;
    
    document.body.appendChild(announcement);
    setTimeout(() => document.body.removeChild(announcement), 1000);
  }

  addFocusStyles(slider) {
    // Add focus styles if not already present
    if (!document.getElementById('announcement-bar-focus-styles')) {
      const style = document.createElement('style');
      style.id = 'announcement-bar-focus-styles';
      style.textContent = `
        .slider--everywhere:focus {
          outline: 2px solid #005fcc;
          outline-offset: 2px;
        }
        
        .slider--everywhere:focus-visible {
          outline: 2px solid #005fcc;
          outline-offset: 2px;
        }
        
        @media (prefers-reduced-motion: reduce) {
          .slider--everywhere {
            scroll-behavior: auto;
          }
        }
      `;
      document.head.appendChild(style);
    }
  }
}

// Initialize when script loads
new AnnouncementBarAccessibility();